import React from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { Spin } from "antd";
import useShopperCartContext from "../providers/ShopperCart";
import locales from "../locales";
import Disclaimer from "../ui/Disclaimer";

import {
  Cart,
  CartItem,
  CartStatus,
  EducationCartSummary,
  PageLayout,
  TotalCartDiscount,
} from "../ui";

const Wrap = styled.div`
  width: 100%;
  .Cart_Summary {
    margin: 2rem 0 0;
  }
  .cta-button-wrapper {
    padding: 32px 0 16px 0;
  }
  .CartSeparator {
    border-top: 1px solid #ddd;
    margin: 12px 0;
  }
  .CartSeparator.first {
    border-top: 1px solid #bbb;
    margin-top: 16px;
  }
  .CardWithMarginTop {
    margin-top: 32px;
  }
  &.education-cart {
    @media screen and (max-width: ${({ theme }) =>
        theme.breakpoints.mobile}px) {
      padding-bottom: 96px;
    }
  }
`;

const LoadingContent = styled.div`
  .loading-content {
    height: 250px;
  }
  .ant-spin {
    position: relative;
    top: 22%;
    left: 46.7%;
    .ant-spin-dot {
      width: 39px;
      height: 39px;
    }
    .ant-spin-dot-item {
      width: 16px;
      height: 16px;
      background-color: ${({ theme }) => theme.palette.accent};
    }
  }
`;

const ShopperCart = () => {
  const intl = useIntl();
  const shopperCartContextRenderData = useShopperCartContext();

  if (!shopperCartContextRenderData) {
    return (
      <PageLayout.Content noSidemenu>
        <Wrap>
          <LoadingContent>
            <div className="loading-content">
              <Spin size="large" />
            </div>
          </LoadingContent>
        </Wrap>
      </PageLayout.Content>
    );
  }

  const currency = shopperCartContextRenderData.merchant_info.currency;
  const locale = shopperCartContextRenderData.merchant_info.countryCode;
  const {
    status,
    link_expired,
    awaiting_manual_screening,
    id_verification_pending,
    id_verification_url,
    cartServices,
    cartItems,
    cartSummary,
    checkoutRedirect,
    products,
    widgetLoaded,
    creditAgreements,
    withCatalogueImages,
    merchant_info,
    showSveaDisclaimer,
    selectedProductCode,
    isEducationMerchant,
  } = shopperCartContextRenderData;

  const isMerchantCancelledSale = link_expired;
  const showCart =
    status !== "confirmed" &&
    !isMerchantCancelledSale &&
    !awaiting_manual_screening &&
    !id_verification_pending;

  const cartServicesBoxes = cartServices.map((service, idx) => {
    const { id, quantity } = service;
    const shopper = {
      quantity,
      text: intl.formatMessage(locales.shopper_quantity_suffix, { quantity }),
    };
    return (
      <div key={id}>
        <React.Fragment key={id}>
          <div className={`CartSeparator${idx === 0 ? " first" : ""}`} />
          <CartItem
            withImage={withCatalogueImages}
            service
            item={service}
            shopper={shopper}
            currency={currency}
            locale={locale}
            merchant={merchant_info.merchant}
          />
        </React.Fragment>
      </div>
    );
  });

  const cartItemsBoxes = cartItems.map((item, idx) => {
    const { id, quantity } = item;
    const shopper = {
      quantity,
      text: intl.formatMessage(locales.shopper_quantity_suffix, { quantity }),
    };
    return (
      <React.Fragment key={id}>
        <div className={`CartSeparator${idx === 0 ? " first" : ""}`} />
        <CartItem
          withImage={withCatalogueImages}
          item={item}
          shopper={shopper}
          currency={currency}
          locale={locale}
          merchant={merchant_info.merchant}
        />
      </React.Fragment>
    );
  });

  const shopper = {
    quantity: cartSummary?.quantity ?? 0,
    text: intl.formatMessage(locales.shopper_quantity_suffix, {
      quantity: cartSummary?.quantity ?? 0,
    }),
  };

  return (
    <PageLayout.Content noSidemenu hideHeaderLogo={isEducationMerchant}>
      <Wrap className={isEducationMerchant ? "education-cart" : ""}>
        {showCart &&
          (isEducationMerchant ? (
            <EducationCartSummary
              merchantName={merchant_info.name}
              merchantLogo={merchant_info.logoUrl}
              courseItems={cartServices.length > 0 ? cartServices : cartItems}
              discount={cartSummary?.discount}
              handling={cartSummary?.handling ?? {}}
              registration={cartSummary?.registration ?? {}}
              setupFee={cartSummary?.setup_fee ?? {}}
              totalAmount={cartSummary?.totalAmount}
              currency={currency}
              locale={locale}
            />
          ) : (
            <Cart
              merchantName={merchant_info.name}
              merchantLogo={merchant_info.logoUrl}
              quantity={cartSummary?.quantity}
              total={cartSummary?.totalAmount}
            >
              {cartServicesBoxes.length > 0
                ? cartServicesBoxes
                : cartItemsBoxes}
              <TotalCartDiscount
                {...cartSummary}
                intl={intl}
                locales={locales}
                shopper={shopper}
              />
            </Cart>
          ))}
        <CartStatus
          cartSummary={cartSummary}
          checkoutRedirect={checkoutRedirect}
          creditAgreements={creditAgreements}
          merchant_name={merchant_info.name}
          merchantLogoUrl={merchant_info.logoUrl}
          storeDescription={merchant_info.storeDescription}
          storePhone={merchant_info.storePhone}
          linkExpired={link_expired}
          awaitingManualScreening={awaiting_manual_screening}
          idVerificationPending={id_verification_pending}
          idVerificationUrl={id_verification_url}
          products={products}
          status={status}
          widgetLoaded={widgetLoaded}
          selectedProductCode={selectedProductCode}
          isEducationMerchant={isEducationMerchant}
        />
        {showSveaDisclaimer && (
          <Disclaimer
            data-testid="disclaimer"
            intl={intl}
            locales={locales}
            widgetLoaded={widgetLoaded}
          />
        )}
      </Wrap>
    </PageLayout.Content>
  );
};

export default ShopperCart;
